import * as Sentry from '@sentry/browser'
import qs from 'qs'

import type { DefaultRootState } from 'react-redux'

import { WebSettings } from 'ducks/apps/App'

import { restartDeveloperSocket } from 'utils/developers'
import { initializeUserflow } from 'utils/userflow'
import {
  getAuthProvider,
  storeMakerAuthProvider,
  storeMakerToken,
} from 'utils/auth'
import { adaloBackendAxios, AxiosResponse } from 'utils/io/http/axios'
import { GOOGLE_AUTH_PROVIDER, MANUAL_AUTH_PROVIDER } from 'config'

const SET_CURRENT_USER = Symbol('SET_CURRENT_USER')
const UPDATE_CURRENT_USER = 'UPDATE_CURRENT_USER'
const SET_CURRENT_USER_TOKEN = Symbol('SET_CURRENT_USER_TOKEN')
const SET_CURRENT_USER_AUTH_PROVIDER = 'SET_CURRENT_USER_AUTH_PROVIDER'
const SET_AUTH_VISIBLE = Symbol('SET_AUTH_VISIBLE')
const SET_TOKEN_REFRESHING = Symbol('SET_TOKEN_REFRESHING')
const REQUEST_USERS = 'REQUEST_USERS'
const REQUEST_USERS_FULFILLED = `${REQUEST_USERS}_FULFILLED`
const UPDATE_CURRENT_USER_FULFILLED = `${UPDATE_CURRENT_USER}_FULFILLED`
const VALIDATE_EMAIL = 'VALIDATE_EMAIL'

export type CurrentUser = User | null
type Users = User[]
type UserToken = string | null
type AuthProdiver = typeof MANUAL_AUTH_PROVIDER | typeof GOOGLE_AUTH_PROVIDER

const authProviders = [MANUAL_AUTH_PROVIDER, GOOGLE_AUTH_PROVIDER]
const checkAuthProvider = (
  authProvider: string | null | undefined
): authProvider is AuthProdiver => {
  if (!authProvider) {
    return false
  }

  return authProviders.includes(authProvider)
}
export interface UserState extends DefaultRootState {
  users: UserInfo
}

interface UserInfo {
  users: Users
  currentUser: CurrentUser
  currentUserToken: UserToken
  authVisible: boolean
  tokenRefreshing: boolean
  authProvider: AuthProdiver | null
}

type InternalFlags = Record<string, boolean | string>

export interface User {
  id: number
  admin: boolean
  name: string
  email: string
  developer: boolean
  createdAt: string
  Organizations: Organization[]
  isAdmin?: boolean
  OrganizationUser: { isAdmin: boolean }
  persona: string
  client: boolean
  internalFlags?: InternalFlags
  emailValidatedAt?: Date
  emailValidationString?: string
  otpEnabled: boolean
  otpBase32?: string | null
}

export interface Organization {
  id: string
  Users: User[]
}
/**
 * Unsure why we have this custom action interface,
 * but because of this we need to define the fulfilled action type manually
 */

interface Action {
  type:
    | typeof SET_CURRENT_USER
    | typeof SET_CURRENT_USER_TOKEN
    | typeof SET_CURRENT_USER_AUTH_PROVIDER
    | typeof SET_AUTH_VISIBLE
    | typeof SET_TOKEN_REFRESHING
    | typeof REQUEST_USERS
    | typeof UPDATE_CURRENT_USER
    | typeof REQUEST_USERS_FULFILLED
    | typeof UPDATE_CURRENT_USER_FULFILLED
    | typeof VALIDATE_EMAIL
  userObject?: User
  token?: string
  authProvider?: string
  refreshing?: boolean
  payload?: Promise<AxiosResponse<UserInfo>>
}

interface ActionResponse extends Omit<Action, 'payload'> {
  payload: {
    data: Users
  }
}

const INITIAL_STATE = {
  users: [],
  currentUser: null,
  currentUserToken: null,
  authVisible: false,
  tokenRefreshing: false,
  authProvider: null,
}

// Reducer

// TODO(cameron) remove
export default (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state = INITIAL_STATE,
  action: ActionResponse
): UserInfo | void => {
  if (action.type === SET_CURRENT_USER) {
    const { userObject } = action

    if (!userObject) {
      return
    }

    window.adaloDeveloper = userObject.developer

    // Setup analytics tools
    Sentry.configureScope(scope => {
      scope.setUser({ email: userObject.email })
    })

    initializeUserflow(userObject).catch(err =>
      console.error('FAILED TO INITIALIZE USERFLOW:', err)
    )

    const mixpanel = window.mixpanel

    if (mixpanel) {
      mixpanel.identify(userObject.id)
      mixpanel.people.set({ email: userObject.email })
    }

    return {
      ...state,
      currentUser: userObject,
      authVisible: false,
    }
  }

  if (action.type === SET_CURRENT_USER_AUTH_PROVIDER) {
    const { authProvider } = action

    if (checkAuthProvider(authProvider)) {
      storeMakerAuthProvider(authProvider)

      return {
        ...state,
        authProvider,
      }
    }

    return {
      ...state,
      authProvider: null,
    }
  }

  if (action.type === SET_CURRENT_USER_TOKEN) {
    const { token } = action

    if (!token) {
      return
    }

    storeMakerToken(token)

    restartDeveloperSocket(token)

    return {
      ...state,
      currentUserToken: token,
      authVisible: false,
      tokenRefreshing: false,
    }
  }

  if (action.type === SET_AUTH_VISIBLE) {
    return {
      ...state,
      authVisible: true,
    }
  }

  if (action.type === SET_TOKEN_REFRESHING) {
    const { refreshing } = action

    return {
      ...state,
      tokenRefreshing: refreshing || false,
    }
  }

  if (action.type === `${REQUEST_USERS}_FULFILLED`) {
    return {
      ...state,
      users: action.payload.data,
    }
  }

  if (action.type === `${UPDATE_CURRENT_USER}_FULFILLED`) {
    const currentUser = action.payload.data[0] || null

    return {
      ...state,
      currentUser,
    }
  }

  return state
}

// Actions

export const setCurrentUser = (userObject: User): Action => ({
  type: SET_CURRENT_USER,
  userObject,
})

interface UpdateUserFields extends Omit<User, 'client'> {
  client: string
  appCategory: string
  dataOption: string
  appLayoutMode: WebSettings['layoutMode']
}

export const updateCurrentUser = (
  userId: number,
  fields: Partial<UpdateUserFields>
): Action => ({
  type: UPDATE_CURRENT_USER,
  payload: adaloBackendAxios.put(`/users/${userId}`, fields),
})

export const setCurrentUserToken = (token: string): Action => ({
  type: SET_CURRENT_USER_TOKEN,
  token,
})

export const setCurrentUserAuthProvider = (
  authProvider: AuthProdiver
): Action => ({
  type: SET_CURRENT_USER_AUTH_PROVIDER,
  authProvider,
})

export const setAuthVisible = (): Action => ({
  type: SET_AUTH_VISIBLE,
})

export const setTokenRefreshing = (refreshing: boolean): Action => ({
  type: SET_TOKEN_REFRESHING,
  refreshing,
})

export const requestUsers = (opts?: { query: string }): Action => ({
  type: REQUEST_USERS,
  payload: adaloBackendAxios.get(`/users?${qs.stringify(opts)}`),
})

export const validateUserEmail = (opts: {
  email: string
  code: string
  callback: () => void
}): Action => {
  const payload = adaloBackendAxios
    .post('/users/validate-email', opts)
    .then(res => {
      opts.callback()

      return res
    })

  return {
    type: VALIDATE_EMAIL,
    payload,
  }
}

// Selectors

export const getCurrentUser = (state: UserState): CurrentUser => {
  return state.users.currentUser
}

export const isCurrentUserAdaloAdmin = (state: UserState): boolean => {
  return !!state.users.currentUser?.admin
}

export const getCurrentUserToken = (state: UserState): UserToken => {
  return state.users.currentUserToken
}
export const getCurrentUserAuthProvider = (
  state: UserState
): AuthProdiver | null => {
  return state.users.authProvider || (getAuthProvider() as AuthProdiver)
}

export const getAuthVisible = (state: UserState): boolean => {
  return state.users.authVisible
}

export const getTokenRefreshing = (state: UserState): boolean => {
  return state.users.tokenRefreshing
}

export const getUsers = (state: UserState): Users => {
  return state.users.users
}

export const userIsAdmin = (
  organization: Organization,
  currentUser: User
): boolean => {
  if (!currentUser) {
    return false
  }

  const members = organization.Users
  const user = members.filter(u => u.id === currentUser.id)[0]

  return user?.OrganizationUser?.isAdmin || currentUser.admin
}
